// Sponsor Dashboard — for teachers / schools / pediatricians who pay for
// PlayFirst Parent seats and gift them to families. Shows the signup code
// to share, seat capacity, redeemed parents, and seat management controls.
//
// Access: any logged-in user whose email matches an existing sponsor record
// (GET /api/sponsors/me returns null otherwise → we show a "register as a
// sponsor" form). Account creation is independent of PFP parent accounts —
// a teacher can sponsor without ever using PFP themselves.
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Copy, Users, GraduationCap, School, Plus, Minus,
  Trash2, Share2, Mail, AlertTriangle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// ---------------------------------------------------------------------------
// Sponsor SIGNUP form (shown if user has no sponsor record yet)
// ---------------------------------------------------------------------------

function SponsorSignupForm({ onCreated, defaultEmail = '', defaultName = '' }) {
  const [form, setForm] = useState({
    owner_name: defaultName,
    owner_email: defaultEmail,
    organisation: '',
    seat_count: 10,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.owner_name?.trim() || !form.owner_email?.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/sponsors`, form, auth());
      toast.success('Sponsor account created — now complete payment to activate seats');
      onCreated(r.data?.sponsor);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not create sponsor account';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-3xl bg-white border border-stone-200 p-6 space-y-4 max-w-lg mx-auto"
      data-testid="sponsor-signup-form"
    >
      <div className="text-center">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 text-[#7C6F9C] flex items-center justify-center mx-auto mb-3">
          <GraduationCap className="w-5 h-5" />
        </div>
        <h2 className="font-serif text-2xl text-[#3a2f4f]">Sponsor PlayFirst Parent for your families</h2>
        <p className="text-sm text-stone-600 mt-1">
          Pre-pay for a pool of seats and share one signup code with the families you want to gift access to.
        </p>
      </div>

      <label className="block">
        <span className="text-xs text-stone-600">Your name</span>
        <input
          required
          value={form.owner_name}
          onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
          className="mt-1 w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
          placeholder="e.g. Ms Naidoo"
          data-testid="sponsor-signup-name"
        />
      </label>

      <label className="block">
        <span className="text-xs text-stone-600">Contact email</span>
        <input
          required
          type="email"
          value={form.owner_email}
          onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
          className="mt-1 w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
          data-testid="sponsor-signup-email"
        />
      </label>

      <label className="block">
        <span className="text-xs text-stone-600">Organisation (optional)</span>
        <input
          value={form.organisation}
          onChange={(e) => setForm({ ...form, organisation: e.target.value })}
          className="mt-1 w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
          placeholder="e.g. Sunshine Pre-School"
          data-testid="sponsor-signup-org"
        />
      </label>

      <label className="block">
        <span className="text-xs text-stone-600">How many family seats?</span>
        <input
          required
          type="number"
          min={1}
          max={500}
          value={form.seat_count}
          onChange={(e) => setForm({ ...form, seat_count: parseInt(e.target.value, 10) || 1 })}
          className="mt-1 w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
          data-testid="sponsor-signup-seats"
        />
        <p className="text-[11px] text-stone-500 mt-1">
          You can grow or shrink this later from your dashboard.
        </p>
      </label>

      <button
        type="submit"
        disabled={busy}
        className="w-full h-12 rounded-2xl bg-[#7C6F9C] hover:bg-[#6a5d8a] text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="sponsor-signup-submit"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        Create sponsor account
      </button>
      <p className="text-[11px] text-center text-stone-500">
        After this step you&apos;ll complete payment, then your code becomes live for families.
      </p>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Sponsor DASHBOARD (shown when a sponsor record exists)
// ---------------------------------------------------------------------------

function SponsorDashboardView({ sponsor, capacity, seats, onRefresh }) {
  const [editingSeats, setEditingSeats] = useState(false);
  const [newSeatCount, setNewSeatCount] = useState(sponsor.seat_count);
  const [saving, setSaving] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(sponsor.signup_code);
      toast.success('Code copied — share it with your families');
    } catch {
      toast.error("Couldn't copy — please copy by hand");
    }
  };

  const shareEmail = () => {
    const subj = encodeURIComponent('Your free PlayFirst Parent access');
    const body = encodeURIComponent(
      `Hi,\n\nI've sponsored PlayFirst Parent for our families. To activate your free access:\n\n`
      + `1. Sign up at https://playfirstapp.com/parent-app/signup\n`
      + `2. On the pricing step, choose "I have a sponsor code" and enter: ${sponsor.signup_code}\n\n`
      + `That's it — your access is on me. Enjoy!\n`,
    );
    window.location.href = `mailto:?subject=${subj}&body=${body}`;
  };

  const saveSeats = async () => {
    if (newSeatCount === sponsor.seat_count) {
      setEditingSeats(false);
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/sponsors/${sponsor.id}/seats`,
        { seat_count: newSeatCount },
        auth(),
      );
      toast.success('Seat count updated');
      setEditingSeats(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update seats");
    } finally {
      setSaving(false);
    }
  };

  const releaseSeat = async (seat) => {
    const who = seat.parent_name || seat.parent_email || 'this parent';
    if (!window.confirm(`Release ${who}'s seat? They'll lose free access.`)) return;
    try {
      await axios.post(
        `${API}/sponsors/${sponsor.id}/seats/${seat.id}/release`,
        {},
        auth(),
      );
      toast.success('Seat released');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't release seat");
    }
  };

  const activeSeats = (seats || []).filter((s) => s.active);

  return (
    <div className="space-y-5 max-w-3xl mx-auto" data-testid="sponsor-dashboard">
      {/* Header & code */}
      <section className="rounded-3xl bg-white border border-stone-200 p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-violet-50 text-[#7C6F9C] flex items-center justify-center flex-shrink-0">
            <School className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-serif text-2xl text-[#3a2f4f] leading-tight">
              {sponsor.organisation || sponsor.owner_name}
            </h2>
            <p className="text-sm text-stone-600 mt-0.5">
              Sponsoring PlayFirst Parent for your families
            </p>
          </div>
        </div>

        {!sponsor.paid && (
          <div className="rounded-2xl bg-amber-50/70 border border-amber-200 p-3 mb-4 flex items-start gap-2" data-testid="sponsor-unpaid-banner">
            <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900">
              <strong>Payment pending.</strong> Your code is reserved but inactive — parents won&apos;t be able to redeem until payment clears. Please complete your Paystack checkout from the email we sent to <span className="font-medium">{sponsor.owner_email}</span>.
            </div>
          </div>
        )}

        <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-rose-50/60 border border-violet-200/70 p-4">
          <p className="text-xs uppercase tracking-wide text-[#7C6F9C] font-medium mb-1">
            Your signup code
          </p>
          <p
            className="font-mono text-3xl tracking-widest text-[#3a2f4f] select-all"
            data-testid="sponsor-code-readout"
          >
            {sponsor.signup_code}
          </p>
          <p className="text-xs text-stone-600 mt-2">
            Share this with the families you want to gift PlayFirst Parent to.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              onClick={copyCode}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-stone-200 hover:bg-stone-50"
              data-testid="sponsor-copy-code"
            >
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
            <button
              onClick={shareEmail}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-stone-200 hover:bg-stone-50"
              data-testid="sponsor-share-email"
            >
              <Mail className="w-3.5 h-3.5" /> Share by email
            </button>
            <button
              onClick={() => {
                navigator.share?.({
                  title: 'Your free PlayFirst Parent access',
                  text: `Sign up at https://playfirstapp.com/parent-app/signup and use code ${sponsor.signup_code}`,
                });
              }}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-stone-200 hover:bg-stone-50"
              data-testid="sponsor-share-native"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
          </div>
        </div>
      </section>

      {/* Capacity */}
      <section className="rounded-3xl bg-white border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#3a2f4f] inline-flex items-center gap-2">
            <Users className="w-4 h-4 text-[#7C6F9C]" /> Seats
          </h3>
          {!editingSeats && (
            <button
              onClick={() => { setNewSeatCount(sponsor.seat_count); setEditingSeats(true); }}
              className="text-xs text-[#7C6F9C] hover:underline"
              data-testid="sponsor-edit-seats"
            >
              Edit
            </button>
          )}
        </div>

        {editingSeats ? (
          <div className="flex items-center gap-2" data-testid="sponsor-edit-seats-row">
            <button
              type="button"
              onClick={() => setNewSeatCount(Math.max(capacity.redeemed, newSeatCount - 1))}
              disabled={newSeatCount <= capacity.redeemed}
              className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 inline-flex items-center justify-center disabled:opacity-40"
              data-testid="sponsor-seats-minus"
            >
              <Minus className="w-4 h-4" />
            </button>
            <input
              type="number"
              min={capacity.redeemed}
              max={500}
              value={newSeatCount}
              onChange={(e) => setNewSeatCount(Math.max(capacity.redeemed, parseInt(e.target.value, 10) || capacity.redeemed))}
              className="w-24 h-10 px-3 rounded-2xl border border-stone-200 text-center font-medium"
              data-testid="sponsor-seats-input"
            />
            <button
              type="button"
              onClick={() => setNewSeatCount(Math.min(500, newSeatCount + 1))}
              className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 inline-flex items-center justify-center"
              data-testid="sponsor-seats-plus"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={saveSeats}
              disabled={saving}
              className="ml-2 h-10 px-4 rounded-2xl bg-[#7C6F9C] hover:bg-[#6a5d8a] text-white text-sm inline-flex items-center gap-1.5"
              data-testid="sponsor-seats-save"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save
            </button>
            <button
              onClick={() => setEditingSeats(false)}
              className="h-10 px-3 rounded-2xl text-sm text-stone-500 hover:bg-stone-50"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Total" value={capacity.total} testid="sponsor-stat-total" />
            <Stat label="Redeemed" value={capacity.redeemed} testid="sponsor-stat-redeemed" />
            <Stat label="Available" value={capacity.available} testid="sponsor-stat-available" />
          </div>
        )}

        <p className="text-[11px] text-stone-500 mt-3">
          You can&apos;t shrink below the number of seats currently in use ({capacity.redeemed}). Release a seat first.
        </p>
      </section>

      {/* Redeemed parents list */}
      <section className="rounded-3xl bg-white border border-stone-200 p-6">
        <h3 className="text-sm font-semibold text-[#3a2f4f] mb-3 inline-flex items-center gap-2">
          <Users className="w-4 h-4 text-[#7C6F9C]" /> Sponsored families
        </h3>
        {activeSeats.length === 0 ? (
          <p className="text-sm text-stone-500 italic py-4" data-testid="sponsor-no-redeemed">
            No families have redeemed yet. Once they enter your code on signup, they&apos;ll appear here.
          </p>
        ) : (
          <ul className="divide-y divide-stone-100" data-testid="sponsor-seat-list">
            {activeSeats.map((seat) => (
              <li
                key={seat.id}
                className="flex items-center justify-between gap-3 py-2.5"
                data-testid={`sponsor-seat-${seat.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#3a2f4f] truncate">
                    {seat.parent_name || seat.parent_email}
                  </p>
                  {seat.redeemed_at && (
                    <p className="text-[11px] text-stone-500">
                      Redeemed {new Date(seat.redeemed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => releaseSeat(seat)}
                  className="text-xs text-rose-600 hover:bg-rose-50 px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                  data-testid={`sponsor-release-${seat.id}`}
                >
                  <Trash2 className="w-3 h-3" /> Release
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-2xl bg-stone-50 p-3 text-center" data-testid={testid}>
      <p className="text-2xl font-semibold text-[#3a2f4f]">{value}</p>
      <p className="text-[11px] text-stone-500 uppercase tracking-wide">{label}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Outer page
// ---------------------------------------------------------------------------

export default function SponsorDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null); // { sponsor, capacity, seats } | { sponsor: null }
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const load = async () => {
    try {
      const [meR, dashR] = await Promise.all([
        axios.get(`${API}/auth/me`, auth()).catch(() => ({ data: null })),
        axios.get(`${API}/sponsors/me`, auth()),
      ]);
      setMe(meR.data || null);
      setData(dashR.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate('/login');
        return;
      }
      toast.error("Couldn't load sponsor dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <Loader2 className="w-6 h-6 animate-spin text-[#7C6F9C]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="px-4 py-3 flex items-center justify-between max-w-3xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-[#7C6F9C] hover:underline"
          data-testid="sponsor-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
        <span className="text-xs text-stone-500">Sponsor Dashboard</span>
      </header>

      <main className="px-4 py-4 md:py-8">
        {!data?.sponsor ? (
          <SponsorSignupForm
            onCreated={() => load()}
            defaultEmail={me?.email || ''}
            defaultName={me?.name || ''}
          />
        ) : (
          <SponsorDashboardView
            sponsor={data.sponsor}
            capacity={data.capacity}
            seats={data.seats}
            onRefresh={load}
          />
        )}
      </main>
    </div>
  );
}
